import { Link, useNavigate } from 'react-router-dom'
import { Menu, X, Moon, Sun, LogOut, User, Clock } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { useTheme } from '../../hooks/useTheme'
import { useAuth } from '../ProtectedRoute'
import { useSessionTimer } from '../../hooks/useSessionTimer'
import SessionTimerDisplay from '../SessionTimerDisplay'
import toast from 'react-hot-toast'

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const { resolvedTheme, toggleTheme } = useTheme()
  const { user, logout, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const userMenuRef = useRef<HTMLDivElement>(null)

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setShowUserMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Session timer setup
  const sessionTimer = useSessionTimer({
    sessionDuration: 1440, // 24 hours (session timer disabled)
    warningTime: 0, // No warning
    onSessionExpired: () => {
      toast.error('세션이 만료되었습니다. 다시 로그인해주세요.', {
        duration: 5000,
        icon: '🔒',
      })
      logout()
      navigate('/login')
    },
    onSessionWarning: remainingTime => {
      console.log(
        `Session warning: ${Math.ceil(remainingTime / 60)} minutes remaining`
      )
    },
  })

  const handleLogout = () => {
    sessionTimer.stopTimer()
    logout()
    navigate('/login')
    toast.success('성공적으로 로그아웃되었습니다.')
  }

  // Determine if we're on the main site or a subdomain
  const isMainSite =
    window.location.hostname === 'shorty.kr' ||
    window.location.hostname === 'localhost'
  const homeUrl = isMainSite ? '/' : 'https://shorty.kr'

  return (
    <header className="sticky top-0 z-50 border-b border-gray-200 bg-white/80 backdrop-blur-md dark:border-gray-700 dark:bg-gray-900/80">
      <div className="container mx-auto px-6">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          {isMainSite ? (
            <Link to="/" className="flex flex-shrink-0 items-center space-x-2">
              <img
                src="/logo.png"
                alt="Shorty.kr"
                width={32}
                height={32}
                className="h-8 w-8 rounded-lg object-contain"
              />
              <span className="hidden text-xl font-bold text-gray-900 dark:text-white sm:block">
                Shorty.kr
              </span>
              <span className="text-lg font-bold text-gray-900 dark:text-white sm:hidden">
                Shorty
              </span>
            </Link>
          ) : (
            <a
              href={homeUrl}
              className="flex flex-shrink-0 items-center space-x-2"
            >
              <img
                src="/logo.png"
                alt="Shorty.kr"
                width={32}
                height={32}
                className="h-8 w-8 rounded-lg object-contain"
              />
              <span className="hidden text-xl font-bold text-gray-900 dark:text-white sm:block">
                Shorty.kr
              </span>
              <span className="text-lg font-bold text-gray-900 dark:text-white sm:hidden">
                Shorty
              </span>
            </a>
          )}

          {/* Desktop Navigation */}
          <nav className="hidden items-center space-x-8 md:flex">
            {isMainSite ? (
              <Link
                to="/"
                className="text-gray-700 hover:text-primary-600 dark:text-gray-300"
              >
                Home
              </Link>
            ) : (
              <a
                href={homeUrl}
                className="text-gray-700 hover:text-primary-600 dark:text-gray-300"
              >
                Home
              </a>
            )}
            <a
              href="https://app.shorty.kr/price"
              className="text-gray-700 hover:text-primary-600 dark:text-gray-300"
            >
              Pricing
            </a>
            <a
              href="https://app.shorty.kr/dashboard"
              className="text-gray-700 hover:text-primary-600 dark:text-gray-300"
            >
              Dashboard
            </a>
            <a
              href="https://app.shorty.kr/bookmarks"
              className="text-gray-700 hover:text-primary-600 dark:text-gray-300"
            >
              Bookmarks
            </a>
            <a
              href="https://app.shorty.kr/team-collaboration"
              className="text-gray-700 hover:text-primary-600 dark:text-gray-300"
            >
              Teams
            </a>
            <a
              href="https://app.shorty.kr/analytics"
              className="text-gray-700 hover:text-primary-600 dark:text-gray-300"
            >
              Analytics
            </a>
          </nav>

          {/* Actions */}
          <div className="flex items-center space-x-2 md:space-x-4">
            {/* Session Timer - only show when authenticated */}
            {isAuthenticated && (
              <>
                {/* Desktop Session Timer */}
                <div className="hidden sm:block">
                  <SessionTimerDisplay
                    remainingTime={sessionTimer.remainingTime}
                    formattedTime={sessionTimer.formattedTime}
                    isWarning={sessionTimer.isWarning}
                    onExtendSession={sessionTimer.extendSession}
                    className="flex"
                    isMobile={false}
                  />
                </div>
                {/* Mobile Session Timer - only show when warning */}
                <div className="block sm:hidden">
                  <SessionTimerDisplay
                    remainingTime={sessionTimer.remainingTime}
                    formattedTime={sessionTimer.formattedTime}
                    isWarning={sessionTimer.isWarning}
                    onExtendSession={sessionTimer.extendSession}
                    className="flex"
                    isMobile={true}
                  />
                </div>
              </>
            )}

            {/* User Menu - only show when authenticated */}
            {isAuthenticated && user && (
              <div className="relative flex-shrink-0" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 rounded-lg p-2 transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-600">
                    <span className="text-sm font-medium text-white">
                      {user.name
                        ? user.name.charAt(0).toUpperCase()
                        : user.email.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="hidden max-w-[120px] truncate text-sm font-medium text-gray-700 dark:text-gray-300 lg:block">
                    {user.name || user.username}
                  </span>
                </button>

                {/* User Dropdown Menu */}
                {showUserMenu && (
                  <div className="absolute right-0 z-50 mt-2 w-48 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                    <div className="py-2">
                      <div className="border-b border-gray-200 px-4 py-2 dark:border-gray-700">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {user.name || user.username}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {user.email}
                        </p>
                        {/* 모바일용 세션 타이머는 제거 - 상단에 항상 표시되므로 불필요 */}
                      </div>
                      <Link
                        to="/profile"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <User className="mr-2 h-4 w-4" />
                        프로필
                      </Link>
                      <button
                        onClick={() => {
                          setShowUserMenu(false)
                          handleLogout()
                        }}
                        className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        로그아웃
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={toggleTheme}
              className="rounded-lg p-2 transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {resolvedTheme === 'dark' ? (
                <Sun className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              ) : (
                <Moon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              )}
            </button>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800 md:hidden"
            >
              {isMenuOpen ? (
                <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              ) : (
                <Menu className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="border-t border-gray-200 py-4 dark:border-gray-700 md:hidden">
            {/* Session Info for Mobile */}
            {isAuthenticated && (
              <div className="mb-3 rounded-lg bg-gray-50 px-4 py-3 dark:bg-gray-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      세션
                    </span>
                  </div>
                  <SessionTimerDisplay
                    remainingTime={sessionTimer.remainingTime}
                    formattedTime={sessionTimer.formattedTime}
                    isWarning={sessionTimer.isWarning}
                    onExtendSession={sessionTimer.extendSession}
                    className="flex"
                    isMobile={false}
                  />
                </div>
              </div>
            )}

            <nav className="flex flex-col space-y-2">
              {isMainSite ? (
                <Link to="/" className="py-2 text-gray-700 dark:text-gray-300">
                  Home
                </Link>
              ) : (
                <a
                  href={homeUrl}
                  className="py-2 text-gray-700 dark:text-gray-300"
                >
                  Home
                </a>
              )}
              <a
                href="https://app.shorty.kr/price"
                className="py-2 text-gray-700 dark:text-gray-300"
              >
                Pricing
              </a>
              <a
                href="https://app.shorty.kr/dashboard"
                className="py-2 text-gray-700 dark:text-gray-300"
              >
                Dashboard
              </a>
              <a
                href="https://app.shorty.kr/bookmarks"
                className="py-2 text-gray-700 dark:text-gray-300"
              >
                Bookmarks
              </a>
              <a
                href="https://app.shorty.kr/team-collaboration"
                className="py-2 text-gray-700 dark:text-gray-300"
              >
                Teams
              </a>
              <a
                href="https://app.shorty.kr/analytics"
                className="py-2 text-gray-700 dark:text-gray-300"
              >
                Analytics
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  )
}
